import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Box, Button, Text, useBreakpointValue } from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import ReactDOM from 'react-dom';
import Script from 'next/script';
import StructuredTextParser from './StructuredTextParser';
import { render } from 'datocms-structured-text-to-html-string';
import SimpleButton from '@components/Buttons/SimpleButton';
import Image from 'next/image';
import { createSectionContentMap } from '../../components/Utility/Shared/SharedService';
import ShambhaviQuote from '@landing_pages/inner-engineering-in-person/Components/Quote/ShambhaviQuote';

interface JwPlayerProps {
  id: string;
  file: string;
  image?: string;
  width?: string | number;
  height?: string | number;
  autoPlay?: boolean;
  aspectRatio?: string;
  repeat?: boolean;
  mute?: boolean;
  controls?: boolean;
  onPlay?: () => void;
  stretching?: string;
}

function JwPlayer({
  id,
  file,
  image,
  width = '100%',
  height = '100%',
  autoPlay = false,
  aspectRatio = '16:9',
  repeat,
  mute,
  controls = true,
  onPlay,
  stretching,
}: JwPlayerProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  const prefixedId = `jw-player-container-${id}`;

  console.log('JW Player file URL:', file);

  // Check for JW Player script readiness on mount and when file changes
  useEffect(() => {
    const checkReady = () => {
      if (window.jwplayer) {
        setScriptReady(true);
      } else {
        // If script is not ready, check again shortly
        setTimeout(checkReady, 100);
      }
    };
    checkReady();
  }, [file]);

  useLayoutEffect(() => {
    if (!id || !scriptReady) {
      console.log('JW Player script not ready, waiting...');
      return;
    }

    let playerInstance: any;

    try {
      playerInstance = window
        .jwplayer(prefixedId)
        .setup({
          file,
          image,
          width,
          height,
          autostart: autoPlay,
          aspectratio: aspectRatio,
          repeat,
          mute,
          controls,
          stretching,
        })
        .on('ready', () => {
          console.log('JW Player ready for ID:', prefixedId);
          setIsLoaded(true);
        })
        .on('play', () => {
          console.log('JW Player started playing');
          onPlay && onPlay();
        })
        .on('error', (e: any) => {
          console.error('JW Player error:', e);
          setScriptError(true);
        })
        .on('setupError', (e: any) => {
          console.error('JW Player setup error:', e);
          setScriptError(true);
        });
    } catch (error) {
      console.error('JW Player initialization failed:', error);
      setScriptError(true);
    }

    // Cleanup function to remove the player instance on component unmount
    return () => {
      if (playerInstance) {
        try {
          playerInstance.remove();
        } catch (e) {
          console.error('Error removing JW Player instance:', e);
        }
      }
    };
  }, [
    id,
    prefixedId,
    scriptReady,
    file,
    autoPlay,
    aspectRatio,
    image,
    width,
    height,
    repeat,
    mute,
    controls,
    stretching,
    onPlay,
  ]);

  return (
    <div
      id={prefixedId}
      style={
        !isLoaded
          ? {
            paddingBottom: aspectRatio === '16:9' ? '56.25%' : '75%',
            backgroundColor: 'black',
            width: '100%',
            height: '100%',
          }
          : { width: '100%', height: '100%' }
      }
    >
      {scriptError && (
        <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
          Failed to load video player. Please check your network or JW Player
          configuration.
        </div>
      )}
    </div>
  );
}

// VideoPopup component
interface VideoPopupProps {
  open: boolean;
  onClose: () => void;
  video: React.ReactNode;
}

const VideoPopup: React.FC<VideoPopupProps> = ({ open, onClose, video }) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
  }, [open]);

  const handleClose = () => {
    console.log('Closing video popup');
    document.body.style.overflow = 'unset';
    // The cleanup function in JwPlayer's useLayoutEffect will handle removal.
    if (window.jwplayer) {
      const player = window.jwplayer();
      if (player && typeof player.remove === 'function') {
        try {
          player.remove();
        } catch (e) {
          /* ignore */
        }
      }
    }
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!isMounted || !open) return null;

  const portalRoot = document.getElementById('__next');
  if (!portalRoot) {
    console.error('Portal root #__next not found');
    return null;
  }

  return ReactDOM.createPortal(
    <Box
      as="div"
      className="video-popup-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        className="video-popup-container"
        style={{
          position: 'relative',
          width: '50vw',
          aspectRatio: '4 / 3',
          overflow: 'hidden',
        }}
      >
        <button
          className="video-popup-close"
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            border: 'none',
            color: '#fff',
            fontSize: '24px',
            cursor: 'pointer',
            zIndex: 10000,
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          <CloseIcon boxSize={3} />
        </button>
        <div
          className="video-player-container"
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {video}
        </div>
      </div>
    </Box>,
    portalRoot
  );
};

// PlayButton component
function PlayButton({
  onClick,
  imageUrl,
}: {
  onClick: () => void;
  imageUrl?: string;
}) {
  return (
    <Box
      position="absolute"
      top="50%"
      left="50%"
      transform="translate(-50%, -50%)"
      // bg="rgba(0, 0, 0, 0.5)"
      p={4}
      cursor="pointer"
      onClick={() => {
        console.log('Play button clicked, opening popup');
        onClick();
      }}
      width={{ base: '50px', md: '70px', lg: '80px' }}
      height={{ base: '50px', md: '70px', lg: '80px' }}
      display="flex"
      alignItems="center"
      justifyContent="center"
      opacity={2}
      transition="all 0.3s"
      boxShadow={'0 4px 8px rgba(0, 0, 0, 0.2)'}
    // _hover={{ opacity: 1 }}
    >
      <Image
        fill
        src={imageUrl || ''}
        alt="Play Button"
        style={{ objectFit: 'contain', width: '100%', height: '100%' }}
      />
    </Box>
  );
}

// ShambhaviVideo component
export function ShambhaviVideo({ autoPlay = false }) {
  const videoId = 'https://cdn.jwplayer.com/manifests/thg2X7TF.m3u8';
  return (
    <JwPlayer
      id={`shambhavi-video-popup-${Math.random()}`}
      file={videoId}
      autoPlay={autoPlay}
      aspectRatio="16:9"
      width="100%"
      height="100%"
      controls={true}
      stretching="fill"
      repeat={true}
    />
  );
}

// TrueMiracle component
const TrueMiracle = ({ sectionContent }) => {
  console.log('Rendering TrueMiracle with content:', sectionContent);
  const [showVideo, setShowVideo] = useState(false);
  // Will be `true` if base (<768px), false if md and above
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Function to insert readmore tags after a certain number of words on mobile
  const insertReadMoreTags = (content, wordLimit = 30) => {
    // Only apply on mobile and if content exists
    if (!content || !isMobile) return content;

    const words = content.split(' ');
    if (words.length <= wordLimit) return content;

    const beforeReadMore = words.slice(0, wordLimit).join(' ');
    const afterReadMore = words.slice(wordLimit).join(' ');

    return `${beforeReadMore} [readmore]${afterReadMore}[/readmore]`;
  };
  const sectionContentMap = createSectionContentMap(sectionContent);

  return (
    <>
      <Script
        src="https://cdn.jwplayer.com/libraries/zdPodMhD.js"
        strategy="lazyOnload"
      />
      <Box bg="#f5f0d9" overflow={'hidden'}>
        <Box
          pl={{ xl: '11%', '2xl': '11%' }}
          display="flex"
          flexDirection={{ base: 'column', sm: 'row' }}
          justifyContent={'center'}
          alignItems="stretch"
          gap={{ base: 8, md: 4, lg: 12 }}
        >
          {/* Text section */}
          <Box
            py={{ base: 5, md: 8, lg: 10 }}
            px={{ base: '16px', sm: '18px', md: '20px', xl: '0px' }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            mx={'auto'}
          >
            {/* the title section */}
            <Box
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              alignItems={{ base: 'center', md: 'flex-start' }}
              gap={2}
              textAlign={{ base: 'center', lg: 'left' }}
              width={{ base: '100%', sm: '100%' }}
              mx={'auto'}
              maxW={'470px'}
              mt={{ base: 3, md: 0 }}
            >
              <Text
                fontFamily={'FedraSansStd-medium'}
                fontWeight={500}
                fontSize={{ base: '32px', md: '32px' }}
                color="#3B444B"
                lineHeight="1.15"
                textAlign={{ base: 'center', md: 'left' }}
                mb={4}
                maxW={{ base: '330px', sm: '417px' }}
              >
                {sectionContentMap['section_title'].titleText}
              </Text>

              <ShambhaviQuote sectionContent={sectionContentMap['quote']} />

              {/* Video embed for mobile */}
              <Box
                mt={4}
                display={{ base: 'block', lg: 'none' }}
                width={{ base: '100%' }}
                height={{ base: '200px', sm: '300px', md: '350px' }}
                minHeight={'200px'}
                alignItems="center"
                justifyContent="center"
                mx="auto"
                position="relative"
                overflow="hidden"
                cursor="pointer"
                onClick={() => setShowVideo(true)}
                mb={4}
              >
                <Image
                  src={sectionContentMap['thumbnail_image'].image.url}
                  alt="Shambhavi Video"
                  fill
                  style={{ objectFit: 'cover' }}
                />
                <PlayButton
                  imageUrl={sectionContentMap['play_icon_image'].image.url}
                  onClick={() => setShowVideo(true)}
                />
              </Box>

              <Box position="relative"
              >
                <StructuredTextParser
                  fontFamily={'FedraSansStd-Book'}
                  inlineReadMoreStyle={'block'}
                  mt="0"
                  fontSize={{ base: '14px', md: '16px' }}
                  str={insertReadMoreTags(render(sectionContent[2]?.body))}
                  sectionContent={sectionContent[2]}
                  textAlign={{ base: 'left', lg: 'left' }}
                  region={'us'}
                  color={'#2B251E'}
                  lang={'en'}
                  maxW="none"
                />
                <Box
                  display={'flex'}
                  justifyContent="flex-start"
                  mt={4}
                  mb={{ base: 6, md: '0' }}
                >
                  <SimpleButton
                    style={sectionContentMap['simple_button']}
                    background={'#F37021 !important'}
                    _hover={{
                      background: '#BC570F !important',
                    }}
                    // width="148px"
                    marginbottom="0"
                    padding={{ md: '14px 25px', base: '14px 21px' }}
                    mb="0"
                    borderRadius="50px"
                    //Todo : add dynamic region and lang
                    region={'us'}
                    lang={'en'}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Video thumbnail for desktop */}
          <Box
            display={{ base: 'none', lg: 'block' }}
            flex={1}
            position="relative"
            overflow="hidden"
            cursor="pointer"
            onClick={() => {
              setShowVideo(true);
            }}
            flexShrink={0}
          >
            <Image
              src={sectionContentMap['thumbnail_image'].image.url}
              alt="Shambhavi Video"
              fill
              style={{ objectFit: 'cover' }}
            />
            <PlayButton
              imageUrl={sectionContentMap['play_icon_image'].image.url}
              onClick={() => setShowVideo(true)}
            />
          </Box>
        </Box>
      </Box>
      {/* Video Popup */}
      <VideoPopup
        open={showVideo}
        onClose={() => setShowVideo(false)}
        video={showVideo ? <ShambhaviVideo autoPlay key={Date.now()} /> : null}
      />

      <style>
        {`
      @media (max-width: 768px) {
        .video-popup-container {
          width: 90vw !important;
        }
      }
    `}
      </style>
    </>
  );
};

export default TrueMiracle;

declare global {
  interface Window {
    jwplayer: any;
  }
}
